import { Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { RouterLink } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { PageShellComponent } from '@shared/components/page-shell/page-shell.component';
import { LocalizedRouterLinkPipe } from '@core/i18n/localized-router-link.pipe';
import { EditorialSelectComponent } from '@features/nuvem/editorial-select.component';
import { PromoBannerComponent } from '@features/nuvem/promo-banner.component';
import { DatastoryService } from './datastory.service';
import {
  DatastorySortKey,
  StoryCard,
  deriveStoryTags,
  filterStoryCards,
  mapBibliotecaToStoryCard,
  sortStoryCards,
} from './datastories.util';
import { AppLocale } from '@core/i18n/locale.constants';

@Component({
  selector: 'app-datastories-page',
  standalone: true,
  imports: [
    TranslocoPipe,
    PageShellComponent,
    LocalizedRouterLinkPipe,
    RouterLink,
    EditorialSelectComponent,
    PromoBannerComponent,
  ],
  templateUrl: './datastories.page.html',
})
export class DatastoriesPage {
  private readonly service = inject(DatastoryService);
  private readonly transloco = inject(TranslocoService);

  private readonly lang = toSignal(this.transloco.langChanges$, {
    initialValue: this.transloco.getActiveLang(),
  });

  private readonly rawStories = toSignal(this.service.getDatastories(), {
    initialValue: [],
  });

  protected readonly breadcrumb = [{ labelKey: 'nav.items.dataStories' }];

  protected readonly sortOptions = computed(() => {
    this.lang();
    return [
      {
        value: 'recent',
        label: this.transloco.translate('pages.dataStories.sortRecent'),
      },
      {
        value: 'popular',
        label: this.transloco.translate('pages.dataStories.sortPopular'),
      },
      {
        value: 'alpha',
        label: this.transloco.translate('pages.dataStories.sortAlpha'),
      },
    ];
  });

  protected readonly query = signal('');
  protected readonly activeTags = signal<string[]>([]);
  protected readonly sort = signal<DatastorySortKey>('recent');

  private readonly cards = computed<StoryCard[]>(() => {
    const locale = (this.lang() as AppLocale) || 'pt-BR';
    return this.rawStories().map((s) => mapBibliotecaToStoryCard(s, locale));
  });

  protected readonly loading = computed(
    () => this.rawStories().length === 0 && this.cards().length === 0,
  );

  protected readonly tagFilters = computed(() => deriveStoryTags(this.cards()));

  protected readonly sortedAll = computed(() =>
    sortStoryCards(this.cards(), 'recent'),
  );

  protected readonly lead = computed(() => this.sortedAll()[0] ?? null);

  protected readonly isFiltering = computed(
    () => this.query().trim().length > 0 || this.activeTags().length > 0,
  );

  protected readonly filtered = computed(() =>
    sortStoryCards(
      filterStoryCards(this.cards(), this.query(), this.activeTags()),
      this.sort(),
    ),
  );

  protected readonly listItems = computed(() => {
    const items = this.filtered();
    if (this.isFiltering()) return items;
    const leadSlug = this.lead()?.link;
    return items.filter((s) => s.link !== leadSlug);
  });

  protected onSearch(value: string): void {
    this.query.set(value);
  }

  protected onSort(value: string): void {
    const key = value as DatastorySortKey;
    this.sort.set(
      key === 'popular' || key === 'alpha' ? key : 'recent',
    );
  }

  protected toggleTag(name: string): void {
    this.activeTags.update((tags) =>
      tags.includes(name) ? tags.filter((t) => t !== name) : [...tags, name],
    );
  }

  protected clearTags(): void {
    this.activeTags.set([]);
  }

  protected clearFilters(): void {
    this.query.set('');
    this.activeTags.set([]);
  }
}
